<template>
  <div class="collection">
    <div class="mb-2">
      <span v-if="models.vars && models.vars.label" class="collection__title">{{ models.vars.label }}</span>
    </div>
    <VueDraggableNext
      :list="itemModel"
      :animation="200"
      :delay="200"
      :delay-on-touch-only="true"
      :disabled="models.vars.allow_sort !== '1'"
      @change="sortCollection($event)"
    >
      <div v-for="(model, index) in itemModel" :key="model.vars.id" class="collection__item">
        <div class="row">
          <div class="collection__item__delete">
            <div v-if="models.vars.allow_sort === '1'" class="collection__item__draggable">
              <FontAwesomeIcon icon="fa-light fa-grip-lines" />
            </div>
            <button
              v-if="models.vars.allow_delete === '1'"
              v-tooltip="{
                title: $t('tooltip.delete'),
              }"
              type="button"
              class="btn btn-danger"
              @click="deleteCollection(index)"
            >
              <FontAwesomeIcon icon="fa-light fa-trash" />
            </button>
          </div>
          <FormFieldComponent
            v-for="children in model.children"
            :key="children.vars.id"
            :model="children"
            @toggle="toggle"
            @update="update"
            @ajaxTargetTrig="ajaxTargetTrig"
            @ajaxTargetLoad="(payload) => ajaxTargetLoad(payload, index)"
          />
        </div>
      </div>
    </VueDraggableNext>
    <div class="d-flex justify-content-end mt-2" v-if="models.vars.allow_add === '1'">
      <button
        v-tooltip="{
          title: $t('tooltip.add'),
        }"
        type="button"
        class="btn btn-success"
        @click="add"
      >
        <FontAwesomeIcon icon="fa-light fa-plus" />
      </button>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { VueDraggableNext } from "vue-draggable-next";
import { defineAsyncComponent } from "vue";

export default {
  name: "CollectionTypeComponent",
  components: {
    VueDraggableNext,
    FormFieldComponent: defineAsyncComponent(() => import("@/components/form/FormFieldComponent.vue")),
    FontAwesomeIcon,
  },
  props: {
    models: {
      type: Object,
      required: true,
    },
  },
  emits: ["toggle", "update", "ajaxTargetTrig", "ajaxTargetLoad", "sortCollection"],
  data() {
    return {
      itemModel: this.models.children,
      collection: {},
    };
  },
  mounted() {
    this.itemModel = this.models.children;
    this.collection = this.models.children && this.models.children.length ? this.models.children[0] : {};
  },
  methods: {
    toggle(model) {
      this.$emit("toggle", model);
    },

    update() {
      this.$emit("update", this.itemModel);
    },

    ajaxTargetTrig(payload) {
      this.$emit("ajaxTargetTrig", payload);
    },

    ajaxTargetLoad(payload, index) {
      this.$emit("ajaxTargetLoad", { ...payload, index: index });
    },

    add() {
      if (this.models.vars.allow_add === "1") {
        const itemToAdd = { vars: {}, children: {} };
        itemToAdd.vars = this.setNewItem(this.collection);

        let childrenToAdd = {};
        Object.entries(this.collection.children).forEach((element) => {
          childrenToAdd[element[0]] = this.setNewItem(element[1]);
        });
        itemToAdd.children = childrenToAdd;
        this.itemModel.push(itemToAdd);
      }
    },

    setNewItem(reference) {
      return {
        vars: {
          id: reference.vars.id + "-" + this.itemModel.length,
          attr: reference.vars.attr ? reference.vars.attr : [],
          block_prefixes: reference.vars.block_prefixes ? reference.vars.block_prefixes : [],
          data: null,
          disabled: reference.vars.disabled ? reference.vars.disabled : false,
          help: reference.vars.help ? reference.vars.help : "",
          label: reference.vars.label ? reference.vars.label : "",
          label_attr: reference.vars.label_attr ? reference.vars.label_attr : [],
          choices: reference.vars.choices ? reference.vars.choices : [],
          multiple: reference.vars.multiple ? reference.vars.multiple : false,
          expanded: reference.vars.expanded ? reference.vars.expanded : false,
          line_break: reference.vars.line_break ? reference.vars.line_break : false,
          name: reference.vars.name + "-" + this.itemModel.length,
          required: reference.vars.required ? reference.vars.required : false,
          row_attr: reference.vars.row_attr ? reference.vars.row_attr : [],
          toggle_class: reference.vars.toggle_class
            ? reference.vars.toggle_class.slice(0, -1) +
              this.itemModel.length +
              "-" +
              reference.vars.toggle_class.slice(-1)
            : "",
          toggler_target: reference.vars.toggler_target
            ? `${reference.vars.toggler_target}-${this.itemModel.length || 0}`
            : "",
          value: reference.vars.name === "position" ? this.itemModel.length : null,
        },
      };
    },

    deleteCollection(index) {
      if (this.models.vars.allow_delete === "1") {
        this.itemModel.splice(index, 1);
        this.$emit("sortCollection");
      }
    },
    sortCollection() {
      if (this.models.vars.allow_sort === "1") {
        this.$emit("sortCollection");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.collection {
  margin-bottom: -10px;
  &__title {
    font-size: 15px;
    font-weight: 700;
    color: var(--primary-color);
  }
  &__item {
    padding: 20px;
    position: relative;
    background: #fff;
    border: 1px solid #e0e6ed;
    box-shadow: 0 0 40px 0 rgb(94 92 154 / 6%);
    border-radius: 6px;
    color: #212529;

    &__delete {
      display: flex;
      justify-content: end;
      position: relative;
    }

    &__draggable {
      cursor: grab;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(50%);
    }
  }
}

.collection__item + .collection__item {
  margin-top: 15px;
}
</style>
